import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { useHistory } from "react-router-dom";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { UserModel } from "../../../../../context_providers/auth/user_model";
import {
  AddressModel,
  CreateReplacementSTPayload,
  ItemRow,
  ItemWarehouseModel,
} from "../../model/warehouse_outward_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import UserSearchFieldV2 from "../../../orders/order_create/customer_section/customer/user_search_widgetV2";
import ItemSearchFieldV2 from "../../../../../erp/inventory/items/item_search_fieldv2";
import {
  CreateReplacementStockTransfer,
  GetAvailableQty,
  GetCustomerBillingAddresses,
  GetCustomerShippingAddresses,
  GetItemWAC,
} from "../../warehouse_module_repo";
import { formatAddress } from "../../warehouse_helper";
import RequiredMark from "../../../../new_components/common/required_mark";
import { validateWarehouseAddress } from "../../../../../utils/warehouse_util";
import { getMissingAddressFields } from "../../../../../utils/location_util";

export default function ReplacementSTFormRawMaterial(): JSX.Element {
  const history = useHistory();
  const { showToast } = useToast();

  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );

  const [customer, setCustomer] = useState<UserModel>();
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [billingAddress, setBillingAddress] = useState<AddressModel | null>(
    null,
  );
  const [shippingAddress, setShippingAddress] = useState<AddressModel | null>(
    null,
  );
  const [billingOptions, setBillingOptions] = useState<AddressModel[]>([]);
  const [shippingOptions, setShippingOptions] = useState<AddressModel[]>([]);

  const [dispatchDate, setDispatchDate] = useState<string>("");
  const [returnDate, setReturnDate] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [logisticPartner, setLogisticPartner] = useState<string>("");
  const [trackingId, setTrackingId] = useState<string>("");
  const [purpose, setPurpose] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const [replacementItems, setReplacementItems] = useState<ItemRow[]>([]);
  const [faultyItems, setFaultyItems] = useState<ItemRow[]>([]);
  const [selectedReplacementItem, setSelectedReplacementItem] = useState<any>();
  const [selectedFaultyItem, setSelectedFaultyItem] = useState<any>();

  const [availableQtyMap, setAvailableQtyMap] = useState<
    Record<string, number>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setReplacementItems([]);
    setFaultyItems([]);
    setSelectedReplacementItem(undefined);
    setSelectedFaultyItem(undefined);
  }, [fromWarehouse?.id]);

  useEffect(() => {
    const fetchAllQty = async () => {
      if (!fromWarehouse?.id) {
        setAvailableQtyMap({});
        return;
      }

      try {
        setLoading(true);
        const res = await GetAvailableQty(fromWarehouse.id);

        if (!res?.success || !res?.data?.item_data) {
          setAvailableQtyMap({});
          return;
        }

        const map: Record<string, number> = {};
        res.data.item_data.forEach((item: any) => {
          map[item.item_code] = item.available_units ?? 0;
        });
        setAvailableQtyMap(map);
      } catch (e) {
        console.error(e);
        setAvailableQtyMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllQty();
  }, [fromWarehouse?.id]);

  useEffect(() => {
    const loadAddresses = async () => {
      if (!customer?.uid) {
        setBillingOptions([]);
        setShippingOptions([]);
        setBillingAddress(null);
        setShippingAddress(null);
        return;
      }

      const [billingRes, shippingRes] = await Promise.all([
        GetCustomerBillingAddresses(customer.uid),
        GetCustomerShippingAddresses(customer.uid),
      ]);

      if (billingRes?.success) {
        setBillingOptions(
          (billingRes.data?.billing_profiles || []).map((p: any) => p.address),
        );
      }
      if (shippingRes?.success) {
        setShippingOptions(
          (shippingRes.data?.billing_profiles || []).map((p: any) => p.address),
        );
      }
    };

    loadAddresses();
  }, [customer?.uid]);

  const fetchAvailableQty = (itemCode: string): number => {
    return availableQtyMap[itemCode] ?? 0;
  };

  const fetchItemWac = async (
    itemCode: string,
  ): Promise<{ value: number; isFetched: boolean }> => {
    try {
      const res = await GetItemWAC(itemCode);
      if (res?.success && typeof res?.data?.wac_value === "number") {
        const value = res.data.wac_value;
        return { value, isFetched: value > 0 };
      }
      return { value: 0, isFetched: false };
    } catch {
      return { value: 0, isFetched: false };
    }
  };

  const handleWarehouseSelect = (warehouse: WarehouseModel | null) => {
    if (!warehouse) {
      setFromWarehouse(null);
      return;
    }

    try {
      validateWarehouseAddress(warehouse.address!);
      setFromWarehouse(warehouse);
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message,
      });
      setFromWarehouse(null);
    }
  };

  const handleCustomerSelect = (c?: UserModel) => {
    setError("");
    setCustomer(c);
    setBillingAddress(null);
    setShippingAddress(null);

    if (!c) {
      setCustomerName("");
      setCustomerId("");
      setContactName("");
      setContactNumber("");
      return;
    }

    const fullName = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    setCustomerName(fullName);
    setCustomerId(c.uid || "");
    setContactName(fullName);
    setContactNumber(c.mobile != null ? String(c.mobile) : "");
  };

  const addItem = async (item: any, type: "replacement" | "faulty") => {
    if (!item) return;
    if (!fromWarehouse?.id) {
      showToast({ type: ToastType.warning, text: "Select From Warehouse" });
      return;
    }

    const availableQty = fetchAvailableQty(item.item_code);
    if (availableQty <= 0) {
      showToast({
        type: ToastType.warning,
        text: "Item is not available in selected warehouse",
      });
      return;
    }

    const wac = await fetchItemWac(item.item_code);
    if (!wac.isFetched) {
      showToast({
        type: ToastType.warning,
        text: "WAC not found. Enter unit price manually.",
      });
    }

    const row: ItemRow = {
      itemCode: item.item_code,
      itemName: item.name,
      quantity: 1,
      unitPrice: wac.value,
      isWacFetched: wac.isFetched,
      availableQty,
      itemUnit: item.unit?.formal_name || "",
      itemHsn: item.tax_detail?.hsn_sac || "",
      subcategory: item.subcategory || "",
    };

    if (type === "replacement") {
      setReplacementItems((prev) => {
        if (prev.some((i) => i.itemCode === row.itemCode)) return prev;
        return [...prev, row];
      });
      return;
    }

    setFaultyItems((prev) => {
      if (prev.some((i) => i.itemCode === row.itemCode)) return prev;
      return [...prev, row];
    });
  };

  const removeReplacementItem = (index: number) =>
    setReplacementItems((p) => p.filter((_, i) => i !== index));

  const removeFaultyItem = (index: number) =>
    setFaultyItems((p) => p.filter((_, i) => i !== index));

  const totalValue = [...replacementItems, ...faultyItems].reduce(
    (t, i) => t + i.quantity * i.unitPrice,
    0,
  );

  const handleCreate = async () => {
    setError("");

    if (!fromWarehouse) return setError("Select warehouse");
    if (!customerId) return setError("Select customer");
    if (!billingAddress) return setError("Select billing address");
    if (!shippingAddress) return setError("Select shipping address");
    {
      const billingMissing = getMissingAddressFields(billingAddress, {
        relaxForNonIndia: true,
      });
      if (billingMissing.length)
        return setError(
          `Billing address is missing ${billingMissing.join(", ")}. Please edit the customer address and try again.`,
        );
      const shippingMissing = getMissingAddressFields(shippingAddress, {
        relaxForNonIndia: true,
      });
      if (shippingMissing.length)
        return setError(
          `Shipping address is missing ${shippingMissing.join(", ")}. Please edit the customer address and try again.`,
        );
    }
    if (!dispatchDate) return setError("Select expected dispatch date");
    if (!returnDate) return setError("Select expected return date");
    if (!contactName.trim()) return setError("Please enter contact name");
    if (!String(contactNumber).trim())
      return setError("Please enter contact number");
    if (String(contactNumber).replace(/\D/g, "").length < 10)
      return setError("Contact number must be at least 10 digits");
    if (!purpose.trim()) return setError("Enter purpose");
    if (!description.trim()) return setError("Enter description");

    if (replacementItems.length === 0)
      return setError("Add at least one replacement item");
    if (faultyItems.length === 0)
      return setError("Add at least one faulty item");
    if (replacementItems.some((i) => i.quantity <= 0))
      return setError("All replacement items must have quantity > 0");
    if (faultyItems.some((i) => i.quantity <= 0))
      return setError("All faulty items must have quantity > 0");
    if ([...replacementItems, ...faultyItems].some((i) => i.unitPrice <= 0))
      return setError("Item can't be added with wac value 0");

    if (replacementItems.some((i) => !i.itemHsn?.trim()))
      return setError("All replacement items must have HSN codes");
    if (faultyItems.some((i) => !i.itemHsn?.trim()))
      return setError("All faulty items must have HSN codes");

    const exceedsAvailable = [...replacementItems, ...faultyItems].some(
      (i) => i.quantity > (i.availableQty ?? 0),
    );
    if (exceedsAvailable) {
      return setError("One or more items exceed available quantity");
    }

    const payload: CreateReplacementSTPayload = {
      complaint_id: "",
      from_warehouse_id: fromWarehouse.id,
      from_warehouse_name: fromWarehouse.name,
      customer_name: customerName,
      customer_id: customerId,
      billing_address: billingAddress ?? undefined,
      shipping_address: shippingAddress ?? undefined,
      expected_date: new Date(dispatchDate),
      expected_dispatch_date: new Date(dispatchDate),
      expected_return_date: new Date(returnDate),
      linked_order_id: "",
      contact_name: contactName,
      contact_number: contactNumber,
      faulty_items: faultyItems.map((i) => ({
        inventoryItemCode: i.itemCode,
        itemName: i.itemName,
        requestedQty: i.quantity,
        itemPrice: i.unitPrice,
        subCategory: i.subcategory,
        itemHSN: i.itemHsn,
        itemUnit: i.itemUnit,
        tax_detail: {
          igst: 0,
          cgst: 0,
          sgst: 0,
        },
        gst_applicable: false,
        deviceIds: [],
      })),
      items: replacementItems.map(
        (i): ItemWarehouseModel => ({
          inventoryItemCode: i.itemCode,
          itemName: i.itemName,
          requestedQty: i.quantity,
          itemPrice: i.unitPrice,
          subCategory: i.subcategory,
          itemHSN: i.itemHsn,
          itemUnit: i.itemUnit,
          tax_detail: {
            igst: 0,
            cgst: 0,
            sgst: 0,
          },
          gst_applicable: false,
        }),
      ),
      description,
      purpose,
      shipping_details: {
        logistic_partner: logisticPartner.trim() || undefined,
        tracking_id: trackingId.trim() || undefined,
      },
      item_type: "RM", // Raw Material type
    };

    try {
      setLoading(true);
      const res = await CreateReplacementStockTransfer(payload);

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "ST Creation Failed",
        });
        return;
      }

      showToast({ type: ToastType.success, text: "ST Created Successfully" });
      history.goBack();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="warehouse-stock-transfer-replacement-rm-form" className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            From Warehouse <RequiredMark />
          </label>
          <WarehouseSearchField
            id="warehouse-stock-transfer-replacement-rm-form-warehouse"
            value={fromWarehouse || undefined}
            onSelect={handleWarehouseSelect}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">
            Customer <RequiredMark />
          </label>
          <UserSearchFieldV2
            id="warehouse-stock-transfer-replacement-rm-form-customer"
            value={customer as any}
            onSelect={(c: any) => handleCustomerSelect(c)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Billing Address <RequiredMark />
          </label>
          <select
            id="warehouse-stock-transfer-replacement-rm-form-billing-address"
            className="h-10 w-full border rounded-md px-3"
            value={billingAddress?.id || ""}
            onChange={(e) => {
              const selected = billingOptions.find(
                (a) => a.id === e.target.value,
              );
              setBillingAddress(selected || null);
            }}
          >
            <option value="">Select billing address</option>
            {billingOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {formatAddress(a)}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">
            Shipping Address <RequiredMark />
          </label>
          <select
            id="warehouse-stock-transfer-replacement-rm-form-shipping-address"
            className="h-10 w-full border rounded-md px-3"
            value={shippingAddress?.id || ""}
            onChange={(e) => {
              const selected = shippingOptions.find(
                (a) => a.id === e.target.value,
              );
              setShippingAddress(selected || null);
            }}
          >
            <option value="">Select shipping address</option>
            {shippingOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {formatAddress(a)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Expected Dispatch Date <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-dispatch-date"
            type="date"
            value={dispatchDate}
            onChange={(e) => setDispatchDate(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Expected Return Date <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-return-date"
            type="date"
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Contact Name <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-contact-name"
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
            placeholder="Enter contact name"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Contact Number <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-contact-number"
            type="text"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
            placeholder="Enter contact number"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Logistic Partner</label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-logistic-partner"
            type="text"
            value={logisticPartner}
            onChange={(e) => setLogisticPartner(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
            placeholder="Enter logistic partner"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Tracking ID</label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-tracking-id"
            type="text"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
            placeholder="Enter tracking ID"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Purpose <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-purpose"
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
            placeholder="Enter purpose"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Description <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-replacement-rm-form-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-10 w-full border rounded-md px-3"
            placeholder="Enter description"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Replacement Items</h2>
          <div className="flex gap-2">
            <div className="flex-1">
              <ItemSearchFieldV2
                id="warehouse-stock-transfer-replacement-rm-form-replacement-item-search"
                disabled={!fromWarehouse}
                value={selectedReplacementItem}
                placeholder="Search item"
                subcategory={[
                  "Raw Materials",
                  "Components",
                  "Others",
                  "JobWork",
                  "Semi finished goods",
                  "Logistics and Transportation",
                ]}
                onSelect={setSelectedReplacementItem}
              />
            </div>
            <button
              id="warehouse-stock-transfer-replacement-rm-form-add-replacement-item-btn"
              type="button"
              onClick={async () => {
                if (!selectedReplacementItem) return;
                await addItem(selectedReplacementItem, "replacement");
                setSelectedReplacementItem(undefined);
              }}
              disabled={loading || !fromWarehouse || !selectedReplacementItem}
              className={`px-4 bg-blue-600 text-white rounded-md flex items-center transition-all ${
                loading || !fromWarehouse || !selectedReplacementItem
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-700"
              }`}
            >
              <Icon path={mdiPlus} size={0.8} />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {replacementItems.map((item, index) => (
            <div
              key={index}
              id={`warehouse-stock-transfer-replacement-rm-form-replacement-item-row-${item.itemCode ?? index}`}
              className="p-4 border rounded-xl bg-gray-50 space-y-3"
            >
              <div className="font-medium">
                {item.itemName} - {item.itemCode}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  id={`warehouse-stock-transfer-replacement-rm-form-replacement-item-row-${item.itemCode ?? index}-quantity`}
                  type="number"
                  min={1}
                  max={item.availableQty}
                  value={item.quantity}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value > item.availableQty) return;
                    setReplacementItems((prev) =>
                      prev.map((it, i) =>
                        i === index ? { ...it, quantity: value } : it,
                      ),
                    );
                  }}
                  className="h-10 border rounded-md px-3"
                  placeholder="Quantity"
                />

                <input
                  id={`warehouse-stock-transfer-replacement-rm-form-replacement-item-row-${item.itemCode ?? index}-unit-price`}
                  type="number"
                  min={0}
                  value={item.unitPrice}
                  disabled={item.isWacFetched ?? item.unitPrice > 0}
                  onChange={(e) => {
                    setReplacementItems((prev) =>
                      prev.map((it, i) =>
                        i === index
                          ? { ...it, unitPrice: Number(e.target.value) }
                          : it,
                      ),
                    );
                  }}
                  className="h-10 border rounded-md px-3"
                  placeholder="Unit Price"
                />
              </div>

              <div className="flex justify-between items-center">
                <p className="text-xs text-green-700 font-medium">
                  Available Units: {item.availableQty}
                </p>

                <button
                  id={`warehouse-stock-transfer-replacement-rm-form-replacement-item-row-${item.itemCode ?? index}-remove-btn`}
                  onClick={() => removeReplacementItem(index)}
                  className="text-red-600"
                >
                  <Icon path={mdiTrashCanOutline} size={0.8} />
                </button>
              </div>
            </div>
          ))}

          {replacementItems.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">
              No Replacement Items added
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Faulty Items</h2>
          <div className="flex gap-2">
            <div className="flex-1">
              <ItemSearchFieldV2
                id="warehouse-stock-transfer-replacement-rm-form-faulty-item-search"
                disabled={!fromWarehouse}
                value={selectedFaultyItem}
                placeholder="Search item"
                subcategory={[
                  "Raw Materials",
                  "Components",
                  "Others",
                  "JobWork",
                  "Semi finished goods",
                  "Logistics and Transportation",
                ]}
                onSelect={setSelectedFaultyItem}
              />
            </div>
            <button
              id="warehouse-stock-transfer-replacement-rm-form-add-faulty-item-btn"
              type="button"
              onClick={async () => {
                if (!selectedFaultyItem) return;
                await addItem(selectedFaultyItem, "faulty");
                setSelectedFaultyItem(undefined);
              }}
              disabled={loading || !fromWarehouse || !selectedFaultyItem}
              className={`px-4 bg-blue-600 text-white rounded-md flex items-center transition-all ${
                loading || !fromWarehouse || !selectedFaultyItem
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-700"
              }`}
            >
              <Icon path={mdiPlus} size={0.8} />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {faultyItems.map((item, index) => (
            <div
              key={index}
              id={`warehouse-stock-transfer-replacement-rm-form-faulty-item-row-${item.itemCode ?? index}`}
              className="p-4 border rounded-xl bg-gray-50 space-y-3"
            >
              <div className="font-medium">
                {item.itemName} - {item.itemCode}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  id={`warehouse-stock-transfer-replacement-rm-form-faulty-item-row-${item.itemCode ?? index}-quantity`}
                  type="number"
                  min={1}
                  max={item.availableQty}
                  value={item.quantity}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value > item.availableQty) return;
                    setFaultyItems((prev) =>
                      prev.map((it, i) =>
                        i === index ? { ...it, quantity: value } : it,
                      ),
                    );
                  }}
                  className="h-10 border rounded-md px-3"
                  placeholder="Quantity"
                />

                <input
                  id={`warehouse-stock-transfer-replacement-rm-form-faulty-item-row-${item.itemCode ?? index}-unit-price`}
                  type="number"
                  min={0}
                  value={item.unitPrice}
                  disabled={item.isWacFetched ?? item.unitPrice > 0}
                  onChange={(e) => {
                    setFaultyItems((prev) =>
                      prev.map((it, i) =>
                        i === index
                          ? { ...it, unitPrice: Number(e.target.value) }
                          : it,
                      ),
                    );
                  }}
                  className="h-10 border rounded-md px-3"
                  placeholder="Unit Price"
                />
              </div>

              <div className="flex justify-between items-center">
                <p className="text-xs text-green-700 font-medium">
                  Available Units: {item.availableQty}
                </p>

                <button
                  id={`warehouse-stock-transfer-replacement-rm-form-faulty-item-row-${item.itemCode ?? index}-remove-btn`}
                  onClick={() => removeFaultyItem(index)}
                  className="text-red-600"
                >
                  <Icon path={mdiTrashCanOutline} size={0.8} />
                </button>
              </div>
            </div>
          ))}

          {faultyItems.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">
              No Faulty Items added
            </p>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>
      )}

      <div className="border-t pt-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="font-semibold">
          Total Value:{" "}
          <span className="text-blue-600">{totalValue.toLocaleString()}</span>
        </div>

        <div className="flex gap-3">
          <button
            id="warehouse-stock-transfer-replacement-rm-form-cancel-btn"
            onClick={() => history.goBack()}
            className="border px-6 py-2 rounded-lg"
          >
            Cancel
          </button>

          <button
            id="warehouse-stock-transfer-replacement-rm-form-submit-btn"
            onClick={handleCreate}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg"
            disabled={loading}
          >
            Create Raw Material Replacement ST
          </button>
        </div>
      </div>
    </div>
  );
}
